import type { UnitDefinition } from '../../../shared/game/world-types.js';

/** Separate quadruped family. Coordinates are metres, Y up, +Z toward the muzzle. */
export const WOLF_LEG_LENGTH_SCALE = 1.22;
export const WOLF_BODY_RISE = (.87 - .065) * (WOLF_LEG_LENGTH_SCALE - 1);
export const WOLF_HEIGHT = 1.48 + WOLF_BODY_RISE;
/** Lengthen the leg above its unchanged paw pivot. */
export const wolfLegHeight = (y: number) => .065 + (y - .065) * WOLF_LEG_LENGTH_SCALE;
/** Authored joint coordinates stay at reference size; presentation is uniformly 30% smaller. */
export const WOLF_VISUAL_SCALE = .7;
export const WOLF_VISUAL_HEIGHT = WOLF_HEIGHT * WOLF_VISUAL_SCALE;
export const WOLF_CLIPS = { idle: 6, combat_idle: 2.4, walk: 1.1, run: .65, attack: .82, flinch: .7, death: 2.1 } as const;
/** Contact times within one cycle, plus the ground distance travelled by that cycle. */
export const WOLF_GAITS = {
  walk: { duty: .69, stride: .90, contacts: { hind_R: 0, front_R: .25, hind_L: .5, front_L: .75 } },
  run: { duty: .24, stride: 2.40, contacts: { hind_L: .06, hind_R: .16, front_L: .52, front_R: .62 } },
} as const;
export type WolfClip = keyof typeof WOLF_CLIPS;
const referenceJoints: [string, string | null, number, number, number][] = [
  ['root', null, 0, 0, 0], ['body', 'root', 0, .88, 0],
  ['pelvis', 'body', 0, .83, -.48],
  ['spine_lumbar', 'pelvis', 0, .89, -.25], ['spine_thoracic', 'spine_lumbar', 0, .91, .08],
  ['chest', 'spine_thoracic', 0, .92, .4],
  ['neck', 'chest', 0, 1.04, .64], ['head', 'neck', 0, 1.22, .91],
  ['jaw', 'head', 0, 1.12, 1.02], ['ear_L', 'head', -.14, 1.34, .86], ['ear_R', 'head', .14, 1.34, .86],
  ['tail_1', 'pelvis', 0, .88, -.75], ['tail_2', 'tail_1', 0, .7, -1.04], ['tail_3', 'tail_2', 0, .42, -1.27],
  ...(['L', 'R'] as const).flatMap((side): [string, string, number, number, number][] => {
    const x = side === 'L' ? -.235 : .235;
    return [
      [`shoulder_${side}`, 'chest', x, .87, .43], [`elbow_${side}`, `shoulder_${side}`, x, .48, .37],
      [`wrist_${side}`, `elbow_${side}`, x, .15, .48], [`front_paw_${side}`, `wrist_${side}`, x, .065, .57],
      [`hip_${side}`, 'pelvis', x, .78, -.5], [`knee_${side}`, `hip_${side}`, x, .48, -.29],
      [`hock_${side}`, `knee_${side}`, x, .25, -.64], [`ankle_${side}`, `hock_${side}`, x, .105, -.56],
      [`hind_paw_${side}`, `ankle_${side}`, x, .065, -.48],
    ];
  }),
];
export const wolfJoints: typeof referenceJoints = referenceJoints.map(([name, parent, x, y, z]) => [name, parent, x,
  name === 'root' || name.includes('_paw_') ? y : /^(shoulder|elbow|wrist|hip|knee|hock|ankle)_/.test(name) ? wolfLegHeight(y) : y + WOLF_BODY_RISE, z]);

/** Visual study only; encounter statistics must come from separately authored monster content. */
export function wolfTemplate(): UnitDefinition {
  return { id: 'wolf-study', name: 'Grey wolf', color: 0xa7b4b8, radius: .65, speed: 3.2, spawn: { x: 0, z: 0 },
    profile: { maxHp: 20, maxWounds: 0, armor: 0, defense: 0, movementDistance: 9,
      attributes: { strength: 0, dexterity: 0, intelligence: 0, will: 0 }, attacks: [], traits: [] },
    appearance: { rig: 'wolf', ancestry: 'beast', archetype: 'raider',
      cloth: 0xffffff, leather: 0xffffff, metal: 0xffffff, skin: 0xffffff, hair: 0xffffff, height: WOLF_VISUAL_HEIGHT },
  };
}
